import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, Tabs, Tab, Card, CardContent, Grid,
  Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, MenuItem, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, Chip, Alert, IconButton, Checkbox, FormControlLabel,
  Autocomplete, CircularProgress, Tooltip, Stack
} from '@mui/material';
import {
  CalendarToday, Schedule, Edit as EditIcon,
  Delete as DeleteIcon, AttachFile as AttachFileIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import api from '../services/api';
import { formatDate } from '../utils/dateFormatter';
import { useAuth } from '../context/AuthContext';
import CompOffClaims from './CompOffClaims';

function EmployeeDashboard() {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [stats, setStats] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [permissions, setPermissions] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [filterType, setFilterType] = useState('monthly');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [dateRangeStart, setDateRangeStart] = useState('');
  const [dateRangeEnd, setDateRangeEnd] = useState('');
  const [compOffFilteredCount, setCompOffFilteredCount] = useState(0);
  const [contents, setContents] = useState([]);

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  // Check if user can see all employee data
  const canSeeAllData = ['owner', 'manager', 'hr', 'lead'].includes(user?.role);

  // Dialogs
  const [leaveDialogOpen, setLeaveDialogOpen] = useState(false);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [permissionSubmitting, setPermissionSubmitting] = useState(false);
  const [workDialogOpen, setWorkDialogOpen] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: null, id: null });
  const [editMode, setEditMode] = useState({ leave: false, permission: false, work: false });
  const [editingId, setEditingId] = useState({ leave: null, permission: null, work: null });
  const [applyForEmployee, setApplyForEmployee] = useState({ leave: false, permission: false, compOff: false });
  const [employees, setEmployees] = useState([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState('');
  const [compOffDialogOpen, setCompOffDialogOpen] = useState(false);
  const [compOffForm, setCompOffForm] = useState({ workDate: '', reason: '', isHalfDay: false });
  const [compOffError, setCompOffError] = useState('');
  const [compOffSubmitting, setCompOffSubmitting] = useState(false);
  const [compOffRefreshKey, setCompOffRefreshKey] = useState(0);
  const [projects, setProjects] = useState([]);
  const [projectSearchInput, setProjectSearchInput] = useState('');
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [leaveError, setLeaveError] = useState('');
  const [permissionError, setPermissionError] = useState('');
  const [workError, setWorkError] = useState('');
  const [workingDaysInfo, setWorkingDaysInfo] = useState(null);

  // Form data
  const [leaveForm, setLeaveForm] = useState({
    leaveType: '',
    startDate: '',
    endDate: '',
    numberOfDays: 1,
    isHalfDay: false,
    reason: ''
  });

  const [permissionForm, setPermissionForm] = useState({
    permissionDate: '',
    startTime: '09:00',
    endTime: '10:00',
    hours: 1,
    reason: ''
  });

  const [workForm, setWorkForm] = useState({
    shootDate: new Date().toISOString().split('T')[0],
    content: '',
    hours: 8,
    myRole: '',
    comments: ''
  });

  // Reimbursement expense state
  const [expenseCategories, setExpenseCategories] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [reimbForm, setReimbForm] = useState({
    date: new Date().toISOString().split('T')[0],
    particulars: '',
    category: 'Shoot Expenses',
    modeOfTransaction: 'G-Pay',
    amount: '',
    vendorName: '',
    remarks: 'Reimbursement'
  });
  const [reimbReceipt, setReimbReceipt] = useState(null);
  const [reimbError, setReimbError] = useState('');
  const [reimbSubmitting, setReimbSubmitting] = useState(false);
  const [reimbSuccess, setReimbSuccess] = useState('');
  const [reimbSubTab, setReimbSubTab] = useState(0);
  const [myExpenses, setMyExpenses] = useState([]);
  const [myExpensesLoading, setMyExpensesLoading] = useState(false);

  // My Salary state
  const [myPayslips, setMyPayslips] = useState([]);
  const [myPayslipsLoading, setMyPayslipsLoading] = useState(false);
  const [myPayslipsError, setMyPayslipsError] = useState('');
  const [salarySelectedMonth, setSalarySelectedMonth] = useState(getCurrentMonth());

  // Manual Punch state
  const [manualPunches, setManualPunches] = useState([]);
  const [manualPunchDialogOpen, setManualPunchDialogOpen] = useState(false);
  const [manualPunchEditId, setManualPunchEditId] = useState(null);
  const [manualPunchSubmitting, setManualPunchSubmitting] = useState(false);
  const [manualPunchError, setManualPunchError] = useState('');
  const [manualPunchForm, setManualPunchForm] = useState({
    punchDate: new Date().toISOString().split('T')[0],
    inTime: '09:30',
    outTime: '18:30',
    hours: '9.00',
    reason: ''
  });

  // Auto-calculate working days for leave (excludes Sundays and holidays)
  useEffect(() => {
    if (leaveForm.startDate && leaveForm.endDate) {
      const fetchWorkingDays = async () => {
        try {
          const response = await api.get('/employee/leaves/calculate-days', {
            params: { startDate: leaveForm.startDate, endDate: leaveForm.endDate }
          });
          const info = response.data.data;
          setWorkingDaysInfo(info);
          const days = leaveForm.isHalfDay ? info.workingDays * 0.5 : info.workingDays;
          setLeaveForm(prev => ({ ...prev, numberOfDays: days }));
        } catch (error) {
          const start = new Date(leaveForm.startDate);
          const end = new Date(leaveForm.endDate);
          const diffDays = Math.ceil(Math.abs(end - start) / (1000 * 60 * 60 * 24)) + 1;
          const days = leaveForm.isHalfDay ? diffDays * 0.5 : diffDays;
          setLeaveForm(prev => ({ ...prev, numberOfDays: days }));
          setWorkingDaysInfo(null);
        }
      };
      fetchWorkingDays();
    }
  }, [leaveForm.startDate, leaveForm.endDate, leaveForm.isHalfDay]);

  // Auto-calculate hours for permission
  useEffect(() => {
    if (permissionForm.startTime && permissionForm.endTime) {
      const [startHour, startMin] = permissionForm.startTime.split(':').map(Number);
      const [endHour, endMin] = permissionForm.endTime.split(':').map(Number);
      const startTotalMins = startHour * 60 + startMin;
      const endTotalMins = endHour * 60 + endMin;
      const diffMins = endTotalMins - startTotalMins;
      const hours = Math.round((diffMins / 60) * 10) / 10;
      setPermissionForm(prev => ({ ...prev, hours: hours > 0 ? hours : 0 }));
    }
  }, [permissionForm.startTime, permissionForm.endTime]);

  useEffect(() => {
    fetchLeaveTypes();
  }, []);

  useEffect(() => {
    if (user?.id) {
      fetchDashboardData();
      fetchProjects();
      fetchManualPunches();
      if (canSeeAllData) {
        fetchEmployees();
      }
    }
  }, [user, selectedMonth, filterType, selectedYear, dateRangeStart, dateRangeEnd]);

  const fetchLeaveTypes = async () => {
    try {
      const response = await api.get('/dropdowns');
      const dropdowns = response.data.data;
      setLeaveTypes((dropdowns.leaveTypes || []).filter(t => t !== 'Comp Off'));
      setExpenseCategories(dropdowns.expenseCategories || []);
      setPaymentMethods(dropdowns.paymentMethods || []);
    } catch (error) {
      console.error('Error fetching leave types:', error);
    }
  };

  function generateMonthOptions() {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const value = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
      options.push({ value, label, isCurrent: i === 0 });
    }
    return options;
  }

  function generateYearOptions() {
    const currentYear = new Date().getFullYear();
    return [
      { value: currentYear.toString(), label: `${currentYear} (Current)` },
      { value: (currentYear - 1).toString(), label: `${currentYear - 1} (Previous)` }
    ];
  }

  function filterByPeriod(items, dateField, endDateField = null) {
    if (filterType === 'daterange' && dateRangeStart && dateRangeEnd) {
      const start = new Date(dateRangeStart);
      const end = new Date(dateRangeEnd + 'T23:59:59');
      return items.filter(item => {
        if (!item[dateField]) return false;
        if (endDateField && item[endDateField]) {
          // Overlap: item period intersects with filter range
          return new Date(item[dateField]) <= end && new Date(item[endDateField]) >= start;
        }
        const d = new Date(item[dateField]);
        return d >= start && d <= end;
      });
    }
    if (filterType === 'yearly') {
      const y = parseInt(selectedYear);
      return items.filter(item => {
        if (!item[dateField]) return false;
        if (endDateField && item[endDateField]) {
          return new Date(item[dateField]).getFullYear() <= y && new Date(item[endDateField]).getFullYear() >= y;
        }
        return new Date(item[dateField]).getFullYear() === y;
      });
    }
    const [year, month] = selectedMonth.split('-');
    const filterStart = new Date(parseInt(year), parseInt(month) - 1, 1);
    const filterEnd = new Date(parseInt(year), parseInt(month), 0, 23, 59, 59);
    return items.filter(item => {
      if (!item[dateField]) return false;
      if (endDateField && item[endDateField]) {
        return new Date(item[dateField]) <= filterEnd && new Date(item[endDateField]) >= filterStart;
      }
      const itemDate = new Date(item[dateField]);
      return itemDate.getFullYear() === parseInt(year) &&
        (itemDate.getMonth() + 1) === parseInt(month);
    });
  }

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/users', { params: { isActive: 'true' } });
      setEmployees(response.data.data.users || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/content/recent-projects');
      const data = response.data.data;
      setProjects(data.titles || []);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setProjects([]);
    }
  };

  const fetchDashboardData = async () => {
    if (filterType === 'daterange' && (!dateRangeStart || !dateRangeEnd)) return;
    try {
      let statsParams;
      if (filterType === 'daterange' && dateRangeStart && dateRangeEnd) {
        statsParams = { filterType: 'daterange', startDate: dateRangeStart, endDate: dateRangeEnd };
      } else if (filterType === 'yearly') {
        statsParams = { year: selectedYear, filterType: 'yearly' };
      } else {
        const [year, month] = selectedMonth.split('-');
        statsParams = { year, month };
      }

      const [statsRes, leavesRes, permissionsRes, contentsRes] = await Promise.all([
        api.get(`/employee/dashboard/stats/${user.id}`, { params: statsParams }),
        api.get(`/employee/leaves/user/${user.id}`, { params: statsParams }),
        api.get(`/employee/permissions/user/${user.id}`, { params: statsParams }),
        api.get(`/employee/manager-dashboard`, { params: statsParams })
      ]);

      const statsData = statsRes.data.data;

      setStats(statsData);
      setLeaves(leavesRes.data.data.leaves);
      setPermissions(permissionsRes.data.data.permissions);
      setContents(contentsRes.data.data?.contents || []);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    }
  };

  const fetchManualPunches = async () => {
    if (!user?.id) return;
    try {
      const res = await api.get(`/employee/manual-punch/user/${user.id}`);
      setManualPunches(res.data.data.punches || []);
    } catch (err) {
      console.error('Error fetching manual punches:', err);
    }
  };

  const handleApplyLeave = async () => {
    setLeaveError('');
    if (applyForEmployee.leave && !selectedEmployeeId) {
      setLeaveError('Please select an employee');
      return;
    }
    if (!leaveForm.leaveType) {
      setLeaveError('Please select a leave type');
      return;
    }
    if (!leaveForm.startDate) {
      setLeaveError('Please select start date');
      return;
    }
    if (!leaveForm.endDate) {
      setLeaveError('Please select end date');
      return;
    }
    if (!leaveForm.reason || leaveForm.reason.trim() === '') {
      setLeaveError('Please provide a reason for the leave');
      return;
    }
    try {
      if (editMode.leave) {
        await api.put(`/employee/leaves/${editingId.leave}`, leaveForm);
      } else {
        const targetUserId = applyForEmployee.leave ? selectedEmployeeId : user.id;
        const targetEmployee = applyForEmployee.leave
          ? employees.find(e => e.id === selectedEmployeeId)
          : user;
        await api.post('/employee/leaves', {
          ...leaveForm,
          userId: targetUserId,
          employeeName: targetEmployee?.fullName,
          appliedBy: user.id,
          appliedByRole: user.role
        });
      }
      setLeaveDialogOpen(false);
      setEditMode({ ...editMode, leave: false });
      setEditingId({ ...editingId, leave: null });
      setApplyForEmployee({ ...applyForEmployee, leave: false });
      setSelectedEmployeeId('');
      setLeaveError('');
      fetchDashboardData();
      setLeaveForm({ leaveType: '', startDate: '', endDate: '', numberOfDays: 1, isHalfDay: false, reason: '' });
    } catch (error) {
      setLeaveError(error.response?.data?.message || 'Error applying leave');
    }
  };

  const handleEditLeave = (leave) => {
    setLeaveForm({
      leaveType: leave.leaveType,
      startDate: leave.startDate,
      endDate: leave.endDate,
      numberOfDays: leave.numberOfDays,
      isHalfDay: leave.isHalfDay || false,
      reason: leave.reason
    });
    setEditMode({ ...editMode, leave: true });
    setEditingId({ ...editingId, leave: leave.id });
    setLeaveDialogOpen(true);
  };

  const handleDeleteLeave = async (id) => {
    try {
      await api.delete(`/employee/leaves/${id}`);
      setDeleteDialog({ open: false, type: null, id: null });
      fetchDashboardData();
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting leave');
    }
  };

  const handleApplyCompOff = async () => {
    setCompOffError('');
    if (applyForEmployee.compOff && !selectedEmployeeId) {
      setCompOffError('Please select an employee');
      return;
    }
    if (!compOffForm.workDate) {
      setCompOffError('Please select a work date');
      return;
    }
    if (!compOffForm.reason.trim()) {
      setCompOffError('Please provide a reason');
      return;
    }
    try {
      setCompOffSubmitting(true);
      const targetUserId = applyForEmployee.compOff ? selectedEmployeeId : user.id;
      await api.post('/comp-off/claim', {
        workDate: compOffForm.workDate,
        reason: compOffForm.reason,
        isHalfDay: compOffForm.isHalfDay,
        userId: targetUserId,
        appliedBy: user.id,
        appliedByRole: user.role
      });
      setCompOffDialogOpen(false);
      setApplyForEmployee({ ...applyForEmployee, compOff: false });
      setSelectedEmployeeId('');
      setCompOffForm({ workDate: '', reason: '', isHalfDay: false });
      setCompOffError('');
      setCompOffRefreshKey(k => k + 1);
    } catch (error) {
      setCompOffError(error.response?.data?.message || 'Error submitting comp-off claim');
    } finally {
      setCompOffSubmitting(false);
    }
  };

  const handleApplyPermission = async () => {
    if (applyForEmployee.permission && !selectedEmployeeId) {
      alert('Please select an employee');
      return;
    }
    if (!permissionForm.permissionDate) {
      alert('Please select permission date');
      return;
    }
    if (!permissionForm.startTime) {
      alert('Please select start time');
      return;
    }
    if (!permissionForm.endTime) {
      alert('Please select end time');
      return;
    }
    if (!permissionForm.reason || permissionForm.reason.trim() === '') {
      alert('Please provide a reason for the permission');
      return;
    }
    try {
      setPermissionSubmitting(true);
      if (editMode.permission) {
        await api.put(`/employee/permissions/${editingId.permission}`, permissionForm);
      } else {
        const targetUserId = applyForEmployee.permission ? selectedEmployeeId : user.id;
        const targetEmployee = applyForEmployee.permission
          ? employees.find(e => e.id === selectedEmployeeId)
          : user;
        await api.post('/employee/permissions', {
          ...permissionForm,
          userId: targetUserId,
          employeeName: targetEmployee?.fullName,
          appliedBy: user.id,
          appliedByRole: user.role
        });
      }
      setPermissionDialogOpen(false);
      setEditMode({ ...editMode, permission: false });
      setEditingId({ ...editingId, permission: null });
      setApplyForEmployee({ ...applyForEmployee, permission: false });
      setSelectedEmployeeId('');
      fetchDashboardData();
      setPermissionForm({ permissionDate: '', startTime: '09:00', endTime: '10:00', hours: 1, reason: '' });
    } catch (error) {
      alert(error.response?.data?.message || 'Error applying permission');
    } finally {
      setPermissionSubmitting(false);
    }
  };

  const handleEditPermission = (permission) => {
    setPermissionForm({
      permissionDate: permission.permissionDate,
      startTime: permission.startTime,
      endTime: permission.endTime,
      hours: permission.hours,
      reason: permission.reason
    });
    setEditMode({ ...editMode, permission: true });
    setEditingId({ ...editingId, permission: permission.id });
    setPermissionDialogOpen(true);
  };

  const handleDeletePermission = async (id) => {
    try {
      await api.delete(`/employee/permissions/${id}`);
      setDeleteDialog({ open: false, type: null, id: null });
      fetchDashboardData();
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting permission');
    }
  };

  // Manual Punch handlers
  const calcManualPunchHours = (inTime, outTime) => {
    if (!inTime || !outTime) return '';
    const [ih, im] = inTime.split(':').map(Number);
    const [oh, om] = outTime.split(':').map(Number);
    if (isNaN(ih) || isNaN(im) || isNaN(oh) || isNaN(om)) return '';
    const mins = (oh * 60 + om) - (ih * 60 + im);
    if (mins <= 0) return '';
    return (mins / 60).toFixed(2);
  };

  // Recalculate hours whenever inTime or outTime changes
  useEffect(() => {
    const hrs = calcManualPunchHours(manualPunchForm.inTime, manualPunchForm.outTime);
    setManualPunchForm(f => ({ ...f, hours: hrs }));
  }, [manualPunchForm.inTime, manualPunchForm.outTime]);

  const openManualPunchDialog = (punch = null) => {
    if (punch) {
      setManualPunchEditId(punch.id);
      setManualPunchForm({
        punchDate: punch.punchDate,
        inTime: punch.inTime,
        outTime: punch.outTime || '',
        hours: punch.hours || '',
        reason: punch.reason
      });
    } else {
      setManualPunchEditId(null);
      setManualPunchForm({
        punchDate: new Date().toISOString().split('T')[0],
        inTime: '09:30',
        outTime: '18:30',
        hours: '9.00',
        reason: ''
      });
    }
    setManualPunchError('');
    setManualPunchDialogOpen(true);
  };

  const handleManualPunchSubmit = async () => {
    const { punchDate, inTime, outTime, reason } = manualPunchForm;
    if (!punchDate || !inTime || !reason.trim()) {
      setManualPunchError('Date, In Time and Reason are required.');
      return;
    }
    if (outTime && outTime <= inTime) {
      setManualPunchError('Out Time must be after In Time.');
      return;
    }
    // Client-side duplicate check
    if (!manualPunchEditId) {
      const dup = manualPunches.find(p =>
        p.punchDate === punchDate && !['cancelled', 'rejected'].includes(p.status)
      );
      if (dup) {
        setManualPunchError(`A request already exists for ${punchDate} (status: ${dup.status}). Please cancel or modify it first.`);
        return;
      }
    }
    setManualPunchSubmitting(true);
    setManualPunchError('');
    try {
      const hours = calcManualPunchHours(inTime, outTime);
      const payload = { ...manualPunchForm, hours: hours || null, userId: user.id };
      if (manualPunchEditId) {
        await api.put(`/employee/manual-punch/${manualPunchEditId}`, payload);
      } else {
        await api.post('/employee/manual-punch', { ...payload, appliedBy: user.id });
      }
      setManualPunchDialogOpen(false);
      fetchManualPunches();
    } catch (err) {
      setManualPunchError(err.response?.data?.message || 'Error saving request');
    } finally {
      setManualPunchSubmitting(false);
    }
  };

  const handleDeleteManualPunch = async (id) => {
    if (!window.confirm('Delete this manual punch request?')) return;
    try {
      await api.delete(`/employee/manual-punch/${id}`);
      fetchManualPunches();
    } catch (err) {
      alert(err.response?.data?.message || 'Error deleting request');
    }
  };

  const handleAddWork = async () => {
    if (!workForm.shootDate) {
      alert('Please select shoot date');
      return;
    }
    if (!workForm.content || workForm.content.trim() === '') {
      alert('Please enter content/project name');
      return;
    }
    if (!workForm.myRole) {
      alert('Please select your role');
      return;
    }
    try {
      if (editMode.work) {
        await api.put(`/employee/daily-work/${editingId.work}`, workForm);
      } else {
        await api.post('/employee/daily-work', { ...workForm, userId: user.id });
      }
      setWorkDialogOpen(false);
      setEditMode({ ...editMode, work: false });
      setEditingId({ ...editingId, work: null });
      fetchDashboardData();
      setWorkForm({
        shootDate: new Date().toISOString().split('T')[0],
        content: '',
        hours: 8,
        myRole: '',
        comments: ''
      });
    } catch (error) {
      alert(error.response?.data?.message || 'Error adding work');
    }
  };

  const handleEditWork = (work) => {
    setWorkForm({
      shootDate: work.shootDate,
      content: work.content,
      hours: work.hours,
      myRole: work.myRole || '',
      comments: work.comments || ''
    });
    setEditMode({ ...editMode, work: true });
    setEditingId({ ...editingId, work: work.id });
    setWorkDialogOpen(true);
  };

  const handleDeleteWork = async (id) => {
    try {
      await api.delete(`/employee/daily-work/${id}`);
      setDeleteDialog({ open: false, type: null, id: null });
      fetchDashboardData();
    } catch (error) {
      alert(error.response?.data?.message || 'Error deleting work');
    }
  };

  const handlePullMyWork = async () => {
    try {
      const response = await api.get(`/employee/daily-work/pull/${user.id}`);
      const { newItems, totalFound, newCount, duplicatesSkipped } = response.data.data;

      if (newCount === 0) {
        alert(
          `No new work items found!\n\n` +
          `Total content items where you're involved: ${totalFound}\n` +
          `Already in worklog: ${duplicatesSkipped}\n` +
          `New items to add: ${newCount}`
        );
        return;
      }

      const confirmAdd = window.confirm(
        `Found ${newCount} new work item(s) from Content Library!\n\n` +
        `Total content found: ${totalFound}\n` +
        `Already in worklog: ${duplicatesSkipped}\n` +
        `New items to add: ${newCount}\n\n` +
        `Click OK to add these items to your worklog.\n` +
        `(Hours default to 0 - you can edit them later)`
      );

      if (!confirmAdd) return;

      await api.post('/employee/daily-work/bulk', { userId: user.id, workItems: newItems });
      await fetchDashboardData();

      alert(
        `Success!\n\nAdded ${newCount} new work items to your worklog.\n\nPlease edit each item to fill in hours and add comments.`
      );
    } catch (error) {
      console.error('Error pulling work:', error);
      alert(error.response?.data?.message || 'Error pulling work from Content Library');
    }
  };

  const confirmDelete = () => {
    const { type, id } = deleteDialog;
    if (type === 'leave') handleDeleteLeave(id);
    else if (type === 'permission') handleDeletePermission(id);
    else if (type === 'work') handleDeleteWork(id);
  };

  // Apply period filtering and sort by date (latest first)
  const filteredLeaves = filterByPeriod(leaves, 'startDate', 'endDate')
    .sort((a, b) => new Date(b.startDate) - new Date(a.startDate));

  const filteredPermissions = filterByPeriod(permissions, 'permissionDate')
    .sort((a, b) => new Date(b.permissionDate) - new Date(a.permissionDate));

  // Name matching for content library (handles "Sridhar" vs "Sridhar Rajagopal")
  const nameMatches = (fieldVal, fullName) => {
    if (!fieldVal || !fullName) return false;
    const a = fieldVal.trim().toLowerCase();
    const b = fullName.trim().toLowerCase();
    return a === b || b.startsWith(a + ' ') || a.startsWith(b + ' ') || b.includes(a) || a.includes(b);
  };
  const CONTENT_ROLE_FIELDS = ['director', 'editor', 'cinematographer', 'writer'];
  const myName = user?.fullName?.trim().toLowerCase();

  const contentWork = myName ? contents.filter(c => {
    const nameMatch = CONTENT_ROLE_FIELDS.some(f => nameMatches(c[f], myName));
    let castingArr = [];
    try { castingArr = typeof c.castingList === 'string' ? JSON.parse(c.castingList) : (c.castingList || []); } catch (e) { castingArr = []; }
    const castingMatch = Array.isArray(castingArr) && castingArr.some(cast => nameMatches(typeof cast === 'string' ? cast : cast?.name || '', myName));
    if (!nameMatch && !castingMatch) return false;
    // Directors/Cinematographers/Actors: Shoot Completed, Editing Completed, Released
    // Editors only: Editing Completed, Released
    const isEditorOnly = nameMatches(c.editor, myName) &&
      !['director', 'cinematographer', 'writer'].some(f => nameMatches(c[f], myName)) &&
      !castingMatch;
    if (isEditorOnly) return ['Editing Completed', 'Released'].includes(c.status);
    return true;
  }).map(c => {
    let castingArr = [];
    try { castingArr = typeof c.castingList === 'string' ? JSON.parse(c.castingList) : (c.castingList || []); } catch (e) { castingArr = []; }
    const roles = CONTENT_ROLE_FIELDS.filter(f => nameMatches(c[f], myName)).map(f => f.charAt(0).toUpperCase() + f.slice(1));
    const castingRole = Array.isArray(castingArr) && castingArr.some(cast => nameMatches(typeof cast === 'string' ? cast : cast?.name || '', myName)) ? ['Actor'] : [];
    return {
      id: `content_${c.id}`,
      shootDate: c.shootDate,
      content: c.contentTitle || c.title || '-',
      myRole: [...roles, ...castingRole].join(', ') || 'Crew',
      contentType: c.contentType || 'Long Video',
      hours: null,
      source: 'content_library',
      contentId: c.contentId,
    };
  }) : [];

  // Work log: Content Library entries only (matches Dashboard performance table logic)
  const myWork = [...contentWork].sort((a, b) => new Date(b.shootDate) - new Date(a.shootDate));

  // Role breakdown for My Worklog
  function buildRoleBreakdown(work) {
    const map = {};
    work.forEach(w => {
      const roles = w.myRole ? w.myRole.split(',').map(r => r.trim()) : ['Unknown'];
      const ct = (w.contentType || 'Long Video').toLowerCase();
      const isShort = ct === 'short' || ct === 'shorts' || ct === 'reel' || ct === 'reels' || ct.startsWith('short');
      roles.forEach(role => {
        if (!role) return;
        if (!map[role]) map[role] = { long: 0, shorts: 0 };
        if (isShort) map[role].shorts += 1;
        else map[role].long += 1;
      });
    });
    return Object.entries(map).map(([role, counts]) => ({ role, long: counts.long, shorts: counts.shorts, total: counts.long + counts.shorts })).sort((a, b) => b.total - a.total);
  }
  const roleBreakdown = buildRoleBreakdown(myWork);
  const totalLong = roleBreakdown.reduce((s, r) => s + r.long, 0);
  const totalShorts = roleBreakdown.reduce((s, r) => s + r.shorts, 0);
  const uniqueLong = myWork.filter(w => { const ct = (w.contentType || 'Long Video').toLowerCase(); return !ct.startsWith('short') && ct !== 'reel' && ct !== 'reels'; }).length;
  const uniqueShorts = myWork.filter(w => { const ct = (w.contentType || '').toLowerCase(); return ct.startsWith('short') || ct === 'reel' || ct === 'reels'; }).length;

  // Calculate totals for filtered data
  const filteredTotalDays = filteredLeaves.reduce((sum, leave) => {
    return sum + (parseFloat(leave.numberOfDays) || 0);
  }, 0);

  const filteredTotalPermissionHours = filteredPermissions.reduce((sum, perm) => {
    return sum + (parseFloat(perm.hours) || 0);
  }, 0);

  // Label for current filter period
  const periodLabel = filterType === 'daterange' && dateRangeStart && dateRangeEnd
    ? `${dateRangeStart} – ${dateRangeEnd}`
    : filterType === 'yearly'
      ? `Year ${selectedYear}`
      : new Date(selectedMonth + '-01').toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  const periodEmptyLabel = filterType === 'daterange' ? 'selected date range' : filterType === 'yearly' ? 'selected year' : 'selected month';

  const fetchMyExpenses = async () => {
    if (!user?.id) return;
    setMyExpensesLoading(true);
    try {
      const res = await api.get('/expenses', { params: { createdBy: user.id, limit: 100 } });
      setMyExpenses(res.data.data.expenses || []);
    } catch (e) {
      console.error('Failed to fetch my expenses:', e);
    } finally {
      setMyExpensesLoading(false);
    }
  };

  const MONTH_NAMES_SALARY = ['January','February','March','April','May','June','July','August','September','October','November','December'];

  const toSalaryMonthYear = (yyyyMM) => {
    const [y, m] = yyyyMM.split('-');
    return `${MONTH_NAMES_SALARY[parseInt(m, 10) - 1]}-${y}`;
  };

  const fetchMyPayslips = async (empId) => {
    if (!empId) return;
    setMyPayslipsLoading(true);
    setMyPayslipsError('');
    try {
      const res = await api.get(`/salary/my-payslips/${empId}`);
      setMyPayslips(res.data.data || []);
    } catch (e) {
      console.error('Failed to fetch payslips:', e);
      setMyPayslips([]);
      setMyPayslipsError(e.response?.data?.message || 'Failed to load salary records');
    } finally {
      setMyPayslipsLoading(false);
    }
  };

  const handleReimbSubmit = async () => {
    setReimbError('');
    if (!reimbForm.particulars.trim()) return setReimbError('Particulars is required.');
    if (!reimbForm.amount || parseFloat(reimbForm.amount) <= 0) return setReimbError('A valid amount is required.');
    if (!reimbReceipt) return setReimbError('Receipt upload is mandatory.');

    setReimbSubmitting(true);
    try {
      const res = await api.post('/expenses', {
        date: reimbForm.date,
        particulars: reimbForm.particulars.trim(),
        category: reimbForm.category,
        modeOfTransaction: reimbForm.modeOfTransaction,
        amount: parseFloat(reimbForm.amount),
        vendorName: reimbForm.vendorName.trim() || undefined,
        remarks: reimbForm.remarks.trim() || undefined,
        transactionType: 'Debit',
        paymentStatus: 'Pending',
        createdBy: user.id
      });
      const expenseId = res.data.data.expense.id;

      const fd = new FormData();
      fd.append('receipt', reimbReceipt);
      await api.post(`/expenses/${expenseId}/receipt`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setReimbSuccess('Reimbursement request submitted successfully.');
      setReimbForm({
        date: new Date().toISOString().split('T')[0],
        particulars: '',
        category: 'Shoot Expenses',
        modeOfTransaction: 'G-Pay',
        amount: '',
        vendorName: '',
        remarks: 'Reimbursement'
      });
      setReimbReceipt(null);
      fetchMyExpenses();
    } catch (e) {
      setReimbError(e.response?.data?.message || 'Failed to submit. Please try again.');
    } finally {
      setReimbSubmitting(false);
    }
  };

  return (
    <Box>
      {/* Header */}
      <Box mb={2}>
        <Typography variant="h5" fontWeight={700} sx={{ fontSize: { xs: '1.2rem', md: '1.75rem' }, mb: 1.5 }}>
          {'\uD83D\uDC64'} {user?.fullName} Dashboard
        </Typography>
        {/* Filter controls \u2014 stack vertically on mobile */}
        <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
          <TextField
            select size="small"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            sx={{ minWidth: 120 }}
            label="Filter by"
          >
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
            <MenuItem value="daterange">Date Range</MenuItem>
          </TextField>
          {filterType === 'monthly' ? (
            <TextField
              select size="small"
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(e.target.value)}
              sx={{ minWidth: { xs: 170, md: 190 } }}
              label="Month"
            >
              {generateMonthOptions().map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}{option.isCurrent ? ' (Current)' : ''}
                </MenuItem>
              ))}
            </TextField>
          ) : filterType === 'yearly' ? (
            <TextField
              select size="small"
              value={selectedYear}
              onChange={(e) => setSelectedYear(e.target.value)}
              sx={{ minWidth: 190 }}
              label="Year"
            >
              {generateYearOptions().map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          ) : (
            <>
              <TextField size="small" type="date" label="From" value={dateRangeStart}
                onChange={(e) => setDateRangeStart(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
              <TextField size="small" type="date" label="To" value={dateRangeEnd}
                onChange={(e) => setDateRangeEnd(e.target.value)}
                InputLabelProps={{ shrink: true }} sx={{ minWidth: 140 }} />
            </>
          )}
        </Box>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={1.5} mb={2}>
          <Grid item xs={6} sm={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, px: 1.5 }}>
                <Box display="flex" alignItems="center" mb={0.5}>
                  <CalendarToday color="primary" sx={{ fontSize: 16 }} />
                  <Typography variant="caption" ml={0.5} fontWeight={600}>Leaves</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', md: '2.125rem' } }}>
                  {filteredTotalDays.toFixed(1)}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>{periodLabel} · Days</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={6} sm={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent sx={{ py: 1.5, px: 1.5 }}>
                <Box display="flex" alignItems="center" mb={0.5}>
                  <Schedule color="secondary" sx={{ fontSize: 16 }} />
                  <Typography variant="caption" ml={0.5} fontWeight={600}>Permissions</Typography>
                </Box>
                <Typography variant="h4" sx={{ fontSize: { xs: '1.6rem', md: '2.125rem' } }}>
                  {filteredTotalPermissionHours.toFixed(1)}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>{periodLabel} · Hrs</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Card sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', height: '100%' }}>
              <CardContent sx={{ py: 1.5, px: 1.5 }}>
                <Box display="flex" alignItems="center" mb={0.5}>
                  <CalendarToday sx={{ color: 'white', fontSize: 16 }} />
                  <Typography variant="caption" ml={0.5} fontWeight={600} sx={{ color: 'white' }}>Comp-Off Balance</Typography>
                </Box>
                <Typography variant="h4" sx={{ color: 'white', fontSize: { xs: '1.6rem', md: '2.125rem' } }}>
                  {Number.isInteger(compOffFilteredCount) ? compOffFilteredCount : compOffFilteredCount.toFixed(1)}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                  {periodLabel} · Days
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tabs — full tabs on desktop, select dropdown on mobile */}
      {(() => {
        const tabLabels = [
          `My Leaves (${filteredTotalDays.toFixed(1)} days)`,
          `Permissions (${filteredTotalPermissionHours.toFixed(1)} hrs)`,
          `Manual Punch (${manualPunches.length})`,
          `Claim Comp Off (${Number.isInteger(compOffFilteredCount) ? compOffFilteredCount : compOffFilteredCount.toFixed(1)} days)`,
          `My Worklog (${myWork.length})`,
          'Reimburse Expense',
          'My Salary'
        ];
        return (
          <Paper sx={{ mb: 2 }}>
            {/* Mobile: select dropdown */}
            <Box sx={{ display: { xs: 'block', md: 'none' }, px: 2, py: 1.5 }}>
              <TextField
                select fullWidth size="small" label="Section"
                value={tabValue}
                onChange={(e) => { const v = Number(e.target.value); setTabValue(v); if (v === 6) fetchMyPayslips(user?.employeeId); }}
              >
                {tabLabels.map((label, idx) => (
                  <MenuItem key={idx} value={idx}>{label}</MenuItem>
                ))}
              </TextField>
            </Box>
            {/* Desktop: full tab bar */}
            <Box sx={{ display: { xs: 'none', md: 'block' } }}>
              <Tabs value={tabValue} onChange={(e, v) => { setTabValue(v); if (v === 6) fetchMyPayslips(user?.employeeId); }} variant="scrollable" scrollButtons="auto">
                {tabLabels.map((label, idx) => (
                  <Tab key={idx} label={label} />
                ))}
              </Tabs>
            </Box>
          </Paper>
        );
      })()}

      {/* Leave Requests Tab */}
      {tabValue === 0 && (
        <Box>
          <Box display="flex" justifyContent="space-between" mb={2} gap={1} flexWrap="wrap" alignItems="center">
            <Typography variant="h6">Leave Requests</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button size="small"
                variant="contained"
                onClick={() => {
                  setApplyForEmployee({ ...applyForEmployee, leave: false });
                  setSelectedEmployeeId('');
                  setLeaveDialogOpen(true);
                }}
              >
                Apply Leave
              </Button>
              {canSeeAllData && (
                <Button size="small"
                  variant="outlined"
                  onClick={() => {
                    setApplyForEmployee({ ...applyForEmployee, leave: true });
                    setSelectedEmployeeId('');
                    setLeaveDialogOpen(true);
                  }}
                >
                  For Employee
                </Button>
              )}
            </Box>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>Period</TableCell>
                  <TableCell>Days</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Reason</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLeaves.map((leave) => (
                  <TableRow
                    key={leave.id}
                    hover
                    onClick={() => leave.status === 'pending' && handleEditLeave(leave)}
                    sx={{ cursor: leave.status === 'pending' ? 'pointer' : 'default' }}
                  >
                    <TableCell>{leave.leaveType}</TableCell>
                    <TableCell>{formatDate(leave.startDate)} to {formatDate(leave.endDate)}</TableCell>
                    <TableCell>{leave.numberOfDays}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{leave.reason}</TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditLeave(leave)}
                        disabled={leave.status !== 'pending'}
                      >
                        <EditIcon />
                      </IconButton>
                      {['owner', 'manager', 'hr'].includes(user?.role) && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, type: 'leave', id: leave.id })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {filteredLeaves.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No leave requests found for the {periodEmptyLabel}.
            </Alert>
          )}
        </Box>
      )}

      {/* Permission Requests Tab */}
      {tabValue === 1 && (
        <Box>
          <Box display="flex" justifyContent="space-between" mb={2} gap={1} flexWrap="wrap" alignItems="center">
            <Typography variant="h6">Permission Requests</Typography>
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button size="small"
                variant="contained"
                onClick={() => {
                  setApplyForEmployee({ ...applyForEmployee, permission: false });
                  setSelectedEmployeeId('');
                  setPermissionDialogOpen(true);
                }}
              >
                Apply Permission
              </Button>
              {canSeeAllData && (
                <Button size="small"
                  variant="outlined"
                  onClick={() => {
                    setApplyForEmployee({ ...applyForEmployee, permission: true });
                    setSelectedEmployeeId('');
                    setPermissionDialogOpen(true);
                  }}
                >
                  For Employee
                </Button>
              )}
            </Box>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Time</TableCell>
                  <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Hours</TableCell>
                  <TableCell>Reason</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredPermissions.map((perm) => (
                  <TableRow
                    key={perm.id}
                    hover
                    onClick={() => perm.status === 'pending' && handleEditPermission(perm)}
                    sx={{ cursor: perm.status === 'pending' ? 'pointer' : 'default' }}
                  >
                    <TableCell>{formatDate(perm.permissionDate)}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{perm.startTime} - {perm.endTime}</TableCell>
                    <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>{perm.hours}</TableCell>
                    <TableCell>{perm.reason}</TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleEditPermission(perm)}
                        disabled={perm.status !== 'pending'}
                      >
                        <EditIcon />
                      </IconButton>
                      {['owner', 'manager', 'hr'].includes(user?.role) && (
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteDialog({ open: true, type: 'permission', id: perm.id })}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {filteredPermissions.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              No permission requests found for the {periodEmptyLabel}.
            </Alert>
          )}
        </Box>
      )}

      {/* Manual Punch Requests Tab */}
      {tabValue === 2 && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={1} flexWrap="wrap">
            <Typography variant="h6">Manual Punch Requests</Typography>
            <Button size="small" variant="contained" startIcon={<Schedule />} onClick={() => openManualPunchDialog()}>
              Request
            </Button>
          </Box>

          {manualPunches.length === 0 ? (
            <Alert severity="info">No manual punch requests found.</Alert>
          ) : (
            <>
              {/* Desktop table */}
              <TableContainer component={Paper} sx={{ display: { xs: 'none', sm: 'block' } }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Date</strong></TableCell>
                      <TableCell><strong>In Time</strong></TableCell>
                      <TableCell><strong>Out Time</strong></TableCell>
                      <TableCell><strong>Hours</strong></TableCell>
                      <TableCell><strong>Reason</strong></TableCell>
                      <TableCell><strong>Status</strong></TableCell>
                      <TableCell align="center"><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {manualPunches.map(p => (
                      <TableRow key={p.id} hover
                        onClick={() => p.status === 'pending' && openManualPunchDialog(p)}
                        sx={{ cursor: p.status === 'pending' ? 'pointer' : 'default' }}
                      >
                        <TableCell>{formatDate(p.punchDate)}</TableCell>
                        <TableCell>{p.inTime}</TableCell>
                        <TableCell>{p.outTime || '—'}</TableCell>
                        <TableCell>{p.hours ? `${p.hours} hrs` : '—'}</TableCell>
                        <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.reason}</TableCell>
                        <TableCell>
                          <Chip label={p.status} size="small"
                            color={p.status === 'approved' ? 'success' : p.status === 'rejected' ? 'error' : p.status === 'cancelled' ? 'default' : 'warning'} />
                          {p.status === 'rejected' && p.rejectionReason && (
                            <Typography variant="caption" color="error" display="block">{p.rejectionReason}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="center" onClick={e => e.stopPropagation()}>
                          <Tooltip title={p.status === 'pending' ? 'Edit' : 'Can only edit pending requests'}>
                            <span>
                              <IconButton size="small" color="primary" disabled={p.status !== 'pending'} onClick={() => openManualPunchDialog(p)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title={p.status === 'pending' ? 'Delete' : 'Can only delete pending requests'}>
                            <span>
                              <IconButton size="small" color="error" disabled={p.status !== 'pending'} onClick={() => handleDeleteManualPunch(p.id)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Mobile cards */}
              <Stack spacing={1.5} sx={{ display: { xs: 'flex', sm: 'none' } }}>
                {manualPunches.map(p => (
                  <Card key={p.id} variant="outlined"
                    onClick={() => p.status === 'pending' && openManualPunchDialog(p)}
                    sx={{ cursor: p.status === 'pending' ? 'pointer' : 'default' }}
                  >
                    <CardContent sx={{ py: 1.5, px: 2, '&:last-child': { pb: 1.5 } }}>
                      <Box display="flex" justifyContent="space-between" alignItems="center">
                        <Box>
                          <Typography variant="subtitle2" fontWeight={700}>{formatDate(p.punchDate)}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {p.inTime}{p.outTime ? ` → ${p.outTime}` : ''}{p.hours ? ` · ${p.hours} hrs` : ''}
                          </Typography>
                        </Box>
                        <Box display="flex" alignItems="center" gap={0.5} onClick={e => e.stopPropagation()}>
                          <Chip label={p.status} size="small"
                            color={p.status === 'approved' ? 'success' : p.status === 'rejected' ? 'error' : p.status === 'cancelled' ? 'default' : 'warning'} />
                          {p.status === 'pending' && (
                            <IconButton size="small" color="error" onClick={() => handleDeleteManualPunch(p.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      </Box>
                      <Typography variant="caption" color="text.secondary" display="block" mt={0.5} noWrap>
                        {p.reason}
                      </Typography>
                      {p.status === 'rejected' && p.rejectionReason && (
                        <Typography variant="caption" color="error" display="block">{p.rejectionReason}</Typography>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </>
          )}
        </Box>
      )}

      {/* Comp-Off Claims Tab - always mounted so filter changes update the count */}
      <Box sx={{ display: tabValue === 3 ? 'block' : 'none' }}>
        <CompOffClaims
          selectedMonth={selectedMonth}
          filterType={filterType}
          selectedYear={selectedYear}
          dateRangeStart={dateRangeStart}
          dateRangeEnd={dateRangeEnd}
          onFilteredCountChange={setCompOffFilteredCount}
          refreshKey={compOffRefreshKey}
          canApplyForEmployee={canSeeAllData}
          onApplyForEmployee={() => {
            setApplyForEmployee({ ...applyForEmployee, compOff: true });
            setSelectedEmployeeId('');
            setCompOffForm({ workDate: '', reason: '', isHalfDay: false });
            setCompOffError('');
            setCompOffDialogOpen(true);
          }}
        />
      </Box>

      {/* Apply Comp-Off on behalf dialog */}
      <Dialog open={compOffDialogOpen} onClose={() => { setCompOffDialogOpen(false); setCompOffError(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>Apply Comp-Off for Employee</DialogTitle>
        <DialogContent>
          {compOffError && <Alert severity="error" sx={{ mb: 2 }}>{compOffError}</Alert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select fullWidth size="small" label="Select Employee"
              value={selectedEmployeeId}
              onChange={e => setSelectedEmployeeId(e.target.value)}
              helperText="Choose employee to apply comp-off on their behalf"
            >
              {employees.map(emp => (
                <MenuItem key={emp.id} value={emp.id}>{emp.fullName}</MenuItem>
              ))}
            </TextField>
            <TextField
              fullWidth size="small" label="Work Date" type="date"
              value={compOffForm.workDate}
              onChange={e => setCompOffForm({ ...compOffForm, workDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Date the employee worked on a holiday/weekly off"
            />
            <TextField
              fullWidth size="small" label="Reason" multiline rows={3}
              value={compOffForm.reason}
              onChange={e => setCompOffForm({ ...compOffForm, reason: e.target.value })}
              placeholder="Describe the work done on this holiday/weekly off"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={compOffForm.isHalfDay}
                  onChange={e => setCompOffForm({ ...compOffForm, isHalfDay: e.target.checked })}
                />
              }
              label="Half Day"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setCompOffDialogOpen(false); setCompOffError(''); }}>Cancel</Button>
          <Button variant="contained" onClick={handleApplyCompOff} disabled={compOffSubmitting}>
            {compOffSubmitting ? 'Submitting…' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* My Worklog Tab */}
      {tabValue === 4 && (
        <Box>
          <Box display="flex" justifyContent="space-between" mb={2} gap={1} flexWrap="wrap">
            <Typography variant="h6">My Worklog</Typography>
          </Box>

          {myWork.length === 0 ? (
            <Alert severity="info">No work entries found for the {periodEmptyLabel}.</Alert>
          ) : (
            <>
              {/* ROLE BREAKDOWN */}
              <Typography variant="subtitle2" fontWeight={700} mb={1} color="text.secondary">ROLE BREAKDOWN</Typography>
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Role</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: 'primary.main' }}>Long</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700, color: 'secondary.main' }}>Shorts</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 700 }}>Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {roleBreakdown.map(row => (
                      <TableRow key={row.role} hover>
                        <TableCell><Chip label={row.role} size="small" variant="outlined" /></TableCell>
                        <TableCell align="center">{row.long > 0 ? <Chip label={row.long} size="small" color="primary" sx={{ minWidth: 36 }} /> : <Typography variant="body2" color="text.disabled">—</Typography>}</TableCell>
                        <TableCell align="center">{row.shorts > 0 ? <Chip label={row.shorts} size="small" color="secondary" sx={{ minWidth: 36 }} /> : <Typography variant="body2" color="text.disabled">—</Typography>}</TableCell>
                        <TableCell align="center"><Chip label={row.total} size="small" sx={{ bgcolor: '#7b1fa2', color: 'white', minWidth: 36 }} /></TableCell>
                      </TableRow>
                    ))}
                    <TableRow sx={{ bgcolor: 'grey.100', borderTop: '2px solid', borderColor: 'divider' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Total (Role-wise)</TableCell>
                      <TableCell align="center"><Chip label={totalLong} size="small" color="primary" sx={{ minWidth: 36, fontWeight: 700 }} /></TableCell>
                      <TableCell align="center"><Chip label={totalShorts} size="small" color="secondary" sx={{ minWidth: 36, fontWeight: 700 }} /></TableCell>
                      <TableCell align="center"><Chip label={totalLong + totalShorts} size="small" sx={{ bgcolor: '#7b1fa2', color: 'white', minWidth: 36, fontWeight: 700 }} /></TableCell>
                    </TableRow>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell sx={{ fontWeight: 700 }}>Total (Unique Content)</TableCell>
                      <TableCell align="center"><Chip label={uniqueLong} size="small" color="primary" variant="outlined" sx={{ minWidth: 36, fontWeight: 700 }} /></TableCell>
                      <TableCell align="center"><Chip label={uniqueShorts} size="small" color="secondary" variant="outlined" sx={{ minWidth: 36, fontWeight: 700 }} /></TableCell>
                      <TableCell align="center"><Chip label={myWork.length} size="small" sx={{ bgcolor: '#7b1fa2', color: 'white', minWidth: 36, fontWeight: 700 }} /></TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>

              {/* DETAILED LOG */}
              <Typography variant="subtitle2" fontWeight={700} mb={1} color="text.secondary">DETAILED LOG</Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>Date</TableCell>
                      <TableCell>Content / Project</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Type</TableCell>
                      <TableCell>Hours</TableCell>
                      <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>Source</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {myWork.map(w => (
                      <TableRow key={w.id} hover>
                        <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDate(w.shootDate)}</TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {w.content}
                            {w.contentId && <Typography component="span" variant="caption" color="text.disabled" ml={0.5}>({w.contentId})</Typography>}
                          </Typography>
                        </TableCell>
                        <TableCell><Chip label={w.myRole || '-'} size="small" variant="outlined" /></TableCell>
                        <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                          {w.contentType ? (
                            <Chip label={w.contentType} size="small" color={['short','shorts','reel','reels','shorts video'].includes((w.contentType||'').toLowerCase()) ? 'secondary' : 'primary'} variant="outlined" />
                          ) : <Chip label="Long" size="small" color="primary" variant="outlined" />}
                        </TableCell>
                        <TableCell>
                          {w.hours != null ? <Chip label={`${w.hours}h`} size="small" color="success" /> : <Typography variant="body2" color="text.disabled">—</Typography>}
                        </TableCell>
                        <TableCell sx={{ display: { xs: 'none', md: 'table-cell' } }}>
                          {w.source === 'content_library'
                            ? <Chip label="Content Library" size="small" sx={{ bgcolor: '#e3f2fd', color: '#1565c0', fontSize: '0.65rem' }} />
                            : <Chip label="Daily Work" size="small" sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontSize: '0.65rem' }} />}
                        </TableCell>
                        <TableCell align="center">
                          {!w.source || w.source !== 'content_library' ? (
                            <Box display="flex" gap={0.5} justifyContent="center">
                              <IconButton size="small" color="primary" onClick={() => handleEditWork(w)}><EditIcon fontSize="small" /></IconButton>
                              <IconButton size="small" color="error" onClick={() => setDeleteDialog({ open: true, type: 'work', id: w.id })}><DeleteIcon fontSize="small" /></IconButton>
                            </Box>
                          ) : (
                            <Typography variant="caption" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </Box>
      )}

      {/* Reimburse Expense Tab */}
      {tabValue === 5 && (
        <Box>
          {/* Sub-tabs */}
          <Tabs
            value={reimbSubTab}
            onChange={(_, v) => {
              setReimbSubTab(v);
              if (v === 1) fetchMyExpenses();
            }}
            sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
          >
            <Tab label="Submit Expense" />
            <Tab label="My Expense Status" />
          </Tabs>

          {/* Sub-tab 0: Submit form */}
          {reimbSubTab === 0 && (
            <Box p={2}>
              {reimbSuccess && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setReimbSuccess('')}>{reimbSuccess}</Alert>
              )}
              {reimbError && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setReimbError('')}>{reimbError}</Alert>
              )}

              <Paper variant="outlined" sx={{ p: 3, maxWidth: 640 }}>
                <Grid container spacing={2}>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth required type="date" label="Date"
                      value={reimbForm.date}
                      onChange={(e) => setReimbForm({ ...reimbForm, date: e.target.value })}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="Transaction Type" value="Debit (Expense)"
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth required multiline rows={2} label="Particulars"
                      value={reimbForm.particulars}
                      onChange={(e) => setReimbForm({ ...reimbForm, particulars: e.target.value })}
                      placeholder="Describe what the expense was for"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select fullWidth required label="Category"
                      value={reimbForm.category}
                      onChange={(e) => setReimbForm({ ...reimbForm, category: e.target.value })}
                    >
                      {(expenseCategories.length > 0
                        ? expenseCategories
                        : ['Shoot Expenses', 'Food', 'Travel', 'Props', 'Equipment', 'Other']
                      ).map(cat => (
                        <MenuItem key={cat} value={cat}>{cat}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      select fullWidth required label="Mode of Transaction"
                      value={reimbForm.modeOfTransaction}
                      onChange={(e) => setReimbForm({ ...reimbForm, modeOfTransaction: e.target.value })}
                    >
                      {(paymentMethods.length > 0
                        ? paymentMethods
                        : ['G-Pay', 'PhonePe', 'Paytm', 'UPI', 'Cash', 'Bank Transfer', 'NEFT', 'IMPS']
                      ).map(m => (
                        <MenuItem key={m} value={m}>{m}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth required type="number" label="Amount"
                      value={reimbForm.amount}
                      onChange={(e) => setReimbForm({ ...reimbForm, amount: e.target.value })}
                      InputProps={{ startAdornment: <Typography sx={{ mr: 0.5, color: 'text.secondary' }}>₹</Typography> }}
                      inputProps={{ min: 0, step: 0.01 }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth label="Payment Status" value="Pending"
                      InputProps={{ readOnly: true }}
                      sx={{ '& .MuiInputBase-input': { color: 'text.secondary' } }}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth label="Vendor / Payee Name"
                      value={reimbForm.vendorName}
                      onChange={(e) => setReimbForm({ ...reimbForm, vendorName: e.target.value })}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      component="label" variant="outlined" fullWidth
                      startIcon={<CloudUploadIcon />}
                      sx={{ justifyContent: 'flex-start', textTransform: 'none', py: 1.5 }}
                      color={reimbReceipt ? 'success' : 'primary'}
                    >
                      {reimbReceipt
                        ? <><AttachFileIcon fontSize="small" sx={{ mr: 0.5 }} />{reimbReceipt.name}</>
                        : 'Upload Receipt *'}
                      <input
                        type="file" hidden accept="image/*,application/pdf"
                        onChange={(e) => { setReimbReceipt(e.target.files[0] || null); setReimbError(''); }}
                      />
                    </Button>
                    <Typography variant="caption" color="text.secondary" ml={0.5}>
                      Receipt is mandatory. Accepted: images, PDF.
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth multiline rows={2} label="Remarks"
                      value={reimbForm.remarks}
                      onChange={(e) => setReimbForm({ ...reimbForm, remarks: e.target.value })}
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      variant="contained" onClick={handleReimbSubmit} disabled={reimbSubmitting}
                      startIcon={reimbSubmitting ? <CircularProgress size={16} color="inherit" /> : null}
                    >
                      {reimbSubmitting ? 'Submitting...' : 'Submit Request'}
                    </Button>
                  </Grid>

                </Grid>
              </Paper>
            </Box>
          )}

          {/* Sub-tab 1: My Expense Status */}
          {reimbSubTab === 1 && (
            <Box p={2}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">My Expense Status</Typography>
                <Button size="small" variant="outlined" onClick={fetchMyExpenses} disabled={myExpensesLoading}>
                  Refresh
                </Button>
              </Box>

              {myExpensesLoading ? (
                <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
              ) : myExpenses.length === 0 ? (
                <Alert severity="info">No expense requests found.</Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell><strong>Date</strong></TableCell>
                        <TableCell><strong>Particulars</strong></TableCell>
                        <TableCell><strong>Category</strong></TableCell>
                        <TableCell><strong>Mode</strong></TableCell>
                        <TableCell align="right"><strong>Amount (₹)</strong></TableCell>
                        <TableCell><strong>Status</strong></TableCell>
                        <TableCell><strong>Remarks</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {myExpenses.map(exp => (
                        <TableRow key={exp.id} hover>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            {new Date(exp.date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </TableCell>
                          <TableCell>{exp.particulars}</TableCell>
                          <TableCell>{exp.category}</TableCell>
                          <TableCell>{exp.modeOfTransaction}</TableCell>
                          <TableCell align="right">{parseFloat(exp.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</TableCell>
                          <TableCell>
                            <Chip
                              label={exp.paymentStatus}
                              size="small"
                              color={
                                exp.paymentStatus === 'Paid' ? 'success' :
                                exp.paymentStatus === 'Pending' ? 'warning' :
                                exp.paymentStatus === 'Partial' ? 'info' : 'default'
                              }
                            />
                          </TableCell>
                          <TableCell>{exp.remarks || '—'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Box>
          )}
        </Box>
      )}

      {/* My Salary Tab */}
      {tabValue === 6 && (
        <Box p={2}>
          {/* Month picker */}
          <Box display="flex" alignItems="center" gap={2} mb={3} flexWrap="wrap">
            <TextField
              select label="Month" size="small" sx={{ minWidth: 180 }}
              value={salarySelectedMonth}
              onChange={(e) => setSalarySelectedMonth(e.target.value)}
            >
              {Array.from({ length: 12 }, (_, i) => {
                const d = new Date();
                d.setMonth(d.getMonth() - i);
                const val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
                const label = d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
                return <MenuItem key={val} value={val}>{label}</MenuItem>;
              })}
            </TextField>
            <Button
              variant="contained" size="small"
              onClick={() => fetchMyPayslips(user?.employeeId)}
              disabled={myPayslipsLoading}
            >
              {myPayslipsLoading ? 'Loading…' : 'View'}
            </Button>
          </Box>

          {myPayslipsLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : myPayslipsError ? (
            <Alert severity="error">{myPayslipsError}</Alert>
          ) : (() => {
            const slip = myPayslips.find(p => p.monthYear === toSalaryMonthYear(salarySelectedMonth));
            if (!slip) return (
              <Alert severity="info">No paid salary record found for {toSalaryMonthYear(salarySelectedMonth)}.</Alert>
            );
            const fmt = (n) => parseFloat(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 });
            const fields = [
              { label: 'Month', value: slip.monthYear },
              { label: 'Working Days', value: slip.workingDays },
              { label: 'Present Days', value: slip.presentDays },
              { label: 'Extra Days', value: slip.extraDays || 0 },
              { label: 'Earned (₹)', value: `₹${fmt(slip.earnedSalary)}` },
              { label: 'Award (₹)', value: `₹${fmt(slip.awardAmount)}` },
              { label: 'Comp-Off Pay (₹)', value: `₹${fmt(slip.compOffPay)}` },
              { label: 'Advance Deduction (₹)', value: `-₹${fmt(slip.advanceDeduction)}` },
              { label: 'Prepaid Deduction (₹)', value: `-₹${fmt(slip.prepaidDeduction)}` },
              { label: 'Other Deductions (₹)', value: `-₹${fmt(slip.otherDeductions)}` },
              { label: 'Net Payable (₹)', value: <Typography fontWeight={700} color="success.main">₹{fmt(slip.netPayable)}</Typography> },
              { label: 'Payment Mode', value: slip.paymentMode || '—' },
              { label: 'Payment Date', value: slip.paymentDate ? new Date(slip.paymentDate).toLocaleDateString('en-IN') : '—' },
              { label: 'Transaction Ref', value: slip.transactionRef || '—' },
            ];
            if (slip.remarks) fields.push({ label: 'Remarks', value: slip.remarks });
            return (
              <Card variant="outlined" sx={{ maxWidth: 560 }}>
                <CardContent>
                  <Typography variant="h6" mb={2}>Salary Slip — {slip.monthYear}</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableBody>
                        {fields.map(({ label, value }) => (
                          <TableRow key={label} sx={{ '&:last-child td': { border: 0 } }}>
                            <TableCell sx={{ color: 'text.secondary', width: '55%', py: 0.8 }}>{label}</TableCell>
                            <TableCell sx={{ fontWeight: label === 'Net Payable (₹)' ? 700 : 400, py: 0.8 }}>{value}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            );
          })()}
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, type: null, id: null })}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          Are you sure you want to delete this {deleteDialog.type}? This action cannot be undone.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, type: null, id: null })}>Cancel</Button>
          <Button onClick={confirmDelete} color="error" variant="contained">Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Leave Dialog */}
      <Dialog open={leaveDialogOpen} onClose={() => { setLeaveDialogOpen(false); setLeaveError(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode.leave ? 'Edit Leave Request' : applyForEmployee.leave ? 'Apply Leave for Employee' : 'Apply for Leave'}
        </DialogTitle>
        <DialogContent>
          {leaveError && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={() => setLeaveError('')}>
              {leaveError}
            </Alert>
          )}
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {applyForEmployee.leave && !editMode.leave && (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Select Employee"
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  helperText="Choose employee to apply leave on their behalf"
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.fullName} ({emp.role})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                select
                fullWidth
                required
                label="Leave Type"
                value={leaveForm.leaveType}
                onChange={(e) => setLeaveForm({ ...leaveForm, leaveType: e.target.value })}
              >
                {leaveTypes.filter(t => t !== 'Comp Off').map((type) => (
                  <MenuItem key={type} value={type}>{type}</MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="Start Date"
                value={leaveForm.startDate}
                onChange={(e) => setLeaveForm({ ...leaveForm, startDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="date"
                label="End Date"
                value={leaveForm.endDate}
                onChange={(e) => setLeaveForm({ ...leaveForm, endDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={leaveForm.isHalfDay}
                    onChange={(e) => setLeaveForm({ ...leaveForm, isHalfDay: e.target.checked })}
                  />
                }
                label="Half Day Leave"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Number of Days (Auto-calculated)"
                value={leaveForm.numberOfDays}
                onChange={(e) => setLeaveForm({ ...leaveForm, numberOfDays: parseFloat(e.target.value) })}
                InputProps={{ readOnly: true }}
                helperText={
                  workingDaysInfo
                    ? [
                        `${workingDaysInfo.workingDays} working day(s) out of ${workingDaysInfo.totalCalendarDays} calendar day(s)`,
                        workingDaysInfo.sundaysSkipped > 0 && `${workingDaysInfo.sundaysSkipped} Sunday(s) excluded`,
                        workingDaysInfo.holidaysSkipped > 0 && `${workingDaysInfo.holidaysSkipped} holiday(s) excluded`,
                        leaveForm.isHalfDay && 'Half day applied'
                      ].filter(Boolean).join(' · ')
                    : leaveForm.isHalfDay
                    ? 'Half day selected — days divided by 2'
                    : 'Automatically calculated (Sundays & holidays excluded)'
                }
              />
              {workingDaysInfo && (workingDaysInfo.sundaysSkipped > 0 || workingDaysInfo.holidaysSkipped > 0) && (
                <Alert severity="info" sx={{ mt: 1, py: 0.5 }}>
                  {[
                    workingDaysInfo.sundaysSkipped > 0 && `${workingDaysInfo.sundaysSkipped} Sunday(s)`,
                    workingDaysInfo.holidaysSkipped > 0 && `${workingDaysInfo.holidaysSkipped} holiday(s)`
                  ].filter(Boolean).join(' and ')} will not be counted as leave days.
                </Alert>
              )}
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Reason"
                multiline
                rows={3}
                value={leaveForm.reason}
                onChange={(e) => setLeaveForm({ ...leaveForm, reason: e.target.value })}
                placeholder="Please provide a reason for your leave request"
                error={!!leaveError && !leaveForm.reason}
                helperText={!!leaveError && !leaveForm.reason ? 'Reason is required' : ''}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setLeaveDialogOpen(false); setLeaveError(''); }}>Cancel</Button>
          <Button onClick={handleApplyLeave} variant="contained">
            {editMode.leave ? 'Update Leave' : 'Apply Leave'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permission Dialog */}
      <Dialog open={permissionDialogOpen} onClose={() => setPermissionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editMode.permission ? 'Edit Permission Request' : applyForEmployee.permission ? 'Apply Permission for Employee' : 'Apply for Permission'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            {applyForEmployee.permission && !editMode.permission && (
              <Grid item xs={12}>
                <TextField
                  select
                  fullWidth
                  required
                  label="Select Employee"
                  value={selectedEmployeeId}
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                  helperText="Choose employee to apply permission on their behalf"
                >
                  {employees.map((emp) => (
                    <MenuItem key={emp.id} value={emp.id}>
                      {emp.fullName} ({emp.role})
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                type="date"
                fullWidth
                required
                label="Permission Date"
                value={permissionForm.permissionDate}
                onChange={(e) => setPermissionForm({ ...permissionForm, permissionDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="time"
                fullWidth
                required
                label="Start Time"
                value={permissionForm.startTime}
                onChange={(e) => setPermissionForm({ ...permissionForm, startTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                type="time"
                fullWidth
                required
                label="End Time"
                value={permissionForm.endTime}
                onChange={(e) => setPermissionForm({ ...permissionForm, endTime: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                type="number"
                label="Hours (Auto-calculated)"
                value={permissionForm.hours}
                InputProps={{ readOnly: true }}
                helperText="Automatically calculated from start and end time"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label="Reason"
                multiline
                rows={3}
                value={permissionForm.reason}
                onChange={(e) => setPermissionForm({ ...permissionForm, reason: e.target.value })}
                placeholder="Please provide a reason for your permission request"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPermissionDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleApplyPermission} variant="contained" disabled={permissionSubmitting}>
            {permissionSubmitting ? 'Submitting...' : editMode.permission ? 'Update Permission' : 'Apply Permission'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Work Dialog */}
      <Dialog open={workDialogOpen} onClose={() => setWorkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editMode.work ? 'Edit Work Log' : 'Add Work Log'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                type="date"
                label="Shoot Date"
                value={workForm.shootDate}
                onChange={(e) => setWorkForm({ ...workForm, shootDate: e.target.value })}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                freeSolo
                options={projects}
                value={workForm.content}
                inputValue={projectSearchInput}
                onInputChange={(e, newValue) => {
                  setProjectSearchInput(newValue);
                  setWorkForm({ ...workForm, content: newValue });
                }}
                onChange={(e, newValue) => {
                  setWorkForm({ ...workForm, content: newValue || '' });
                  setProjectSearchInput(newValue || '');
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    fullWidth
                    required
                    label="Content"
                    helperText="Project name or content description (max 300 characters)"
                    inputProps={{ ...params.inputProps, maxLength: 300 }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                type="number"
                label="Hours"
                value={workForm.hours}
                onChange={(e) => setWorkForm({ ...workForm, hours: parseFloat(e.target.value) })}
                inputProps={{ min: 0, max: 24, step: 0.5 }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                select
                fullWidth
                required
                label="My Role"
                value={workForm.myRole}
                onChange={(e) => setWorkForm({ ...workForm, myRole: e.target.value })}
              >
                <MenuItem value="Actor">Actor</MenuItem>
                <MenuItem value="Cinematographer">Cinematographer</MenuItem>
                <MenuItem value="Editor">Editor</MenuItem>
                <MenuItem value="Director">Director</MenuItem>
                <MenuItem value="Writer">Writer</MenuItem>
              </TextField>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Comments"
                value={workForm.comments}
                onChange={(e) => setWorkForm({ ...workForm, comments: e.target.value })}
                helperText="Optional: Additional notes or comments"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setWorkDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddWork} variant="contained">
            {editMode.work ? 'Update' : 'Add'} Worklog
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Punch Dialog */}
      <Dialog open={manualPunchDialogOpen} onClose={() => setManualPunchDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{manualPunchEditId ? 'Edit Manual Punch Request' : 'Request Manual Punch'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            {manualPunchError && (
              <Grid item xs={12}>
                <Alert severity="error">{manualPunchError}</Alert>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth required
                label="Date"
                type="date"
                value={manualPunchForm.punchDate}
                onChange={e => setManualPunchForm(f => ({ ...f, punchDate: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required
                label="In Time"
                type="time"
                value={manualPunchForm.inTime}
                onChange={e => setManualPunchForm(f => ({ ...f, inTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Out Time"
                type="time"
                value={manualPunchForm.outTime}
                onChange={e => setManualPunchForm(f => ({ ...f, outTime: e.target.value }))}
                InputLabelProps={{ shrink: true }}
                helperText="Optional — leave blank if only IN punch is needed"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Hours"
                value={manualPunchForm.hours ? `${manualPunchForm.hours} hrs` : (manualPunchForm.inTime && manualPunchForm.outTime ? '⚠ Out before In' : '—')}
                InputProps={{ readOnly: true }}
                helperText="Auto-calculated from In / Out time"
                sx={{ '& .MuiInputBase-input': { color: manualPunchForm.hours ? 'success.main' : (manualPunchForm.inTime && manualPunchForm.outTime ? 'error.main' : 'text.disabled'), fontWeight: 600 } }}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth required multiline rows={3}
                label="Reason"
                placeholder="Describe why the manual punch is needed..."
                value={manualPunchForm.reason}
                onChange={e => setManualPunchForm(f => ({ ...f, reason: e.target.value }))}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setManualPunchDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleManualPunchSubmit} variant="contained" disabled={manualPunchSubmitting}>
            {manualPunchSubmitting ? 'Saving…' : manualPunchEditId ? 'Update' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmployeeDashboard;
